/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormikHelpers } from 'formik';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import {
    FormStepStatusDto,
    ValidationProblemDetails,
    ContactClient,
    ContactFormStep,
} from '../../../api/web-api-client';
import { tokenRequest } from '../../../authentication/authConfig';
import { ErrorType, WizardFormStepValues, WizardStepProps } from '../../../components/forms/WizardForm/types';
import { DiscardProps } from '../../../components/forms/FormikForm/types';
import { setDashboardNotification } from '../../../storage/notification';
import { NotificationSeverity } from '../../../storage/types';
import { AccountContextState } from '../../../authentication/accountContext';
import { HttpStatusCode } from '../../../types';
import contactSubmitValidation from '../validation';

const loadContactDetails = (accounts: AccountInfo[], instance: IPublicClientApplication, accountId: number) => async (
    abortSignal?: AbortSignal,
) => {
    if (accounts.length > 0) {
        const client = new ContactClient();
        const tokenResult = await instance.acquireTokenSilent({
            ...tokenRequest,
            account: accounts[0],
        });
        client.setAuthToken(tokenResult.accessToken);

        const businessDetailsStep = await client.getUserContact(abortSignal);

        const wizardStepValues: WizardFormStepValues<ContactFormStep> = {
            stepValues: { ...businessDetailsStep },
        };

        return wizardStepValues;
    }

    throw Error('There was an error retrieving your contact details.');
};

const completeContactDetails = (accounts: AccountInfo[], instance: IPublicClientApplication, accountContext: AccountContextState | null) => async (
    values: ContactFormStep,
    isDirty: boolean,
    _: FormikHelpers<ContactFormStep>,
    abortSignal?: AbortSignal,
) => {
    if (accounts.length > 0) {
        try {
            const client = new ContactClient();
            const tokenResult = await instance.acquireTokenSilent({
                ...tokenRequest,
                account: accounts[0],
            });
            client.setAuthToken(tokenResult.accessToken);
            await client.saveContactDetails(
                {
                    // formId: 0,
                    formStep: {
                        ...values,
                    },
                    // interim fix for address lookup update,
                    // as using touched/setFieldTouched/setFieldValue doesn't seem to trigger
                    // formik isDirty for the autosuggest unless you blur another control after selection before submission
                    // isDirty: true,
                },
                abortSignal,
            );
            await accountContext?.setContactCompleted();
            setDashboardNotification({
                message: 'Your contact details have been successfully saved.',
                severity: NotificationSeverity.Success,
            });
        } catch (error) {
            const problemDetails = error as ValidationProblemDetails;
            if (problemDetails) {
                switch (problemDetails.status) {
                    case HttpStatusCode.PreconditionFailed:
                        setDashboardNotification({
                            message: 'This branch/location name already exists. Please enter a unique branch/location name.',
                            severity: NotificationSeverity.Error,
                        });
                        break;
                    default:
                        setDashboardNotification({
                            message: 'There was an error saving your organisation.',
                            severity: NotificationSeverity.Error,
                        });
                }
            }
        }
    } else {
        throw Error('There was an error saving your organisation details.');
    }
};

// const cancelSave = () => {
//     setDashboardNotification({
//         message: 'Your organisation and contact details have not been updated.',
//         severity: NotificationSeverity.Information,
//     });
// };

const discardChanges: DiscardProps = {
    cancelButtonTitle: 'Cancel',
    discardButtonTitle: 'Cancel',
    showCancelButton: true,
    locationOnDiscard: '/sign-out',
    // onDiscard: cancelSave,
};

const getRedirectionLocationOnError = (errorCode: number, errorType: ErrorType) => {
    if (errorCode === 412) {
        return '/';
    }

    return undefined;
};

const createContactProps = (
    accounts: AccountInfo[],
    instance: IPublicClientApplication,
    statuses: FormStepStatusDto[],
    accountContext: AccountContextState | null,
    contactId: number,
)
: WizardStepProps<ContactFormStep> => ({
    initialValues: {
    },
    stepStatuses: statuses,
    loadStepValues: loadContactDetails(accounts, instance, contactId),
    location: '/',
    title: 'My contact details',
    hidingFields: {
        contact: {
            titleOther: (x) => x.contact?.title !== 'Other',
        },
    },
    validateHard: contactSubmitValidation,
    onSaveAndNext: completeContactDetails(accounts, instance, accountContext),
    bannerTitle: 'Create portal account',
    discard: discardChanges,
    getRedirectionLocationOnError,
});

export default createContactProps;
